import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Copy, ExternalLink, LoaderCircle, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  changeMarketplaceAppDomainApi,
  deleteMarketplaceAppApi,
  fetchMarketplaceAppApi,
  fetchMarketplaceAppStatsApi,
  renameMarketplaceAppApi,
  startMarketplaceAppApi,
  stopMarketplaceAppApi,
  updateMarketplaceAppResourcesApi,
  readyAppAssetUrl,
  readyAppDisplayTitle
} from '../../api/marketplaceApps.api';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/Button';
import { copyTextToClipboard } from '../../features/chat/MessageActions';
import { AppInstanceLogs } from '../../features/apps/AppInstanceLogs';
import { occupiesPublishSlot, hasPublishSlotRemaining } from '../../features/apps/readyAppQuota';
import { isDatabaseReadyApp } from '../../features/apps/readyAppDisplay';
import { useI18n } from '../../hooks/useI18n';
import { refreshMyPackage, useMyPackage } from '../../hooks/useMyPackage';
import { classNames } from '../../utils/classNames';

const PENDING_STATUSES = new Set(['creating', 'deleting']);
const SUBDOMAIN_REGEX = /^(?=.{3,63}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

const statusTone = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'running') {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300';
  }
  if (normalized === 'stopped') {
    return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
  }
  if (normalized === 'failed') {
    return 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300';
  }
  return 'bg-sky-50 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300';
};

const statusLabel = (status, t) => {
  const key = `readyAppsStatus${(status || '').charAt(0).toUpperCase()}${(status || '').slice(1)}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const usagePercent = (used, limit) => {
  if (!(used > 0) || !(limit > 0)) {
    return 0;
  }
  return Math.min(100, Math.round((used / limit) * 100));
};

export const AppInstancePage = () => {
  const { instanceId = '' } = useParams();
  const navigate = useNavigate();
  const { t, language, formatDate } = useI18n();
  const BackIcon = language === 'fa' ? ArrowRight : ArrowLeft;
  const entitlement = useMyPackage();
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState('');
  const [pollUntil, setPollUntil] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDomain, setEditingDomain] = useState(false);
  const [domainDraft, setDomainDraft] = useState('');
  const [cpuDraft, setCpuDraft] = useState(1);
  const [memoryDraft, setMemoryDraft] = useState(1024);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    if (!instanceId) {
      return;
    }

    try {
      const next = await fetchMarketplaceAppApi(instanceId);
      setInstance(next);
      setCpuDraft(Number(next.cpuLimit || 1));
      setMemoryDraft(Number(next.memoryMb || 1024));
      setError('');
    } catch (loadError) {
      setError(loadError?.message || t('readyAppsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [instanceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const status = (instance?.status || '').toLowerCase();
  const shouldPoll = PENDING_STATUSES.has(status) || pollUntil > Date.now();

  useEffect(() => {
    if (pollUntil <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => setPollUntil(0), Math.max(0, pollUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [pollUntil]);

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }

    const timer = window.setInterval(load, 4000);
    return () => window.clearInterval(timer);
  }, [load, shouldPoll]);

  useEffect(() => {
    if (!instanceId || status !== 'running') {
      return undefined;
    }

    let cancelled = false;
    const loadStats = async () => {
      try {
        const next = await fetchMarketplaceAppStatsApi(instanceId);
        if (!cancelled) {
          setStats(next);
        }
      } catch {
        if (!cancelled) {
          setStats(null);
        }
      }
    };

    loadStats();
    const timer = window.setInterval(loadStats, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [instanceId, status]);

  const runAction = async (key, action, { poll = false } = {}) => {
    setBusy(key);
    setActionError('');
    try {
      const next = await action();
      if (next) {
        setInstance(next);
        if (next.cpuLimit) {
          setCpuDraft(Number(next.cpuLimit));
        }
        if (next.memoryMb) {
          setMemoryDraft(Number(next.memoryMb));
        }
      }
      await refreshMyPackage();
      if (poll) {
        setPollUntil(Date.now() + 20000);
      }
      return true;
    } catch (actionErr) {
      setActionError(actionErr?.message || t('readyAppsActionError'));
      return false;
    } finally {
      setBusy('');
    }
  };

  const copyValue = async (key, value) => {
    if (!value) {
      return;
    }

    try {
      await copyTextToClipboard(String(value));
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1600);
    } catch {
      setActionError(t('readyAppsCopyError'));
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('readyAppsTitle')}>
        <div className="space-y-4">
          <div className="h-40 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900" />
          <div className="h-[420px] animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !instance) {
    return (
      <DashboardLayout title={t('readyAppsTitle')}>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-6 dark:border-rose-900/40 dark:bg-rose-900/20">
          <p className="text-sm text-rose-700 dark:text-rose-200">{error || t('readyAppsNotFound')}</p>
          <Link
            to="/cloud?tab=apps"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-rose-800 dark:text-rose-100"
          >
            <BackIcon size={16} />
            {t('readyAppsBack')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const title = readyAppDisplayTitle(instance, language);
  const isRunning = status === 'running';
  const isPending = PENDING_STATUSES.has(status);
  const isBusy = Boolean(busy) || isPending;
  const startNeedsSlot = !isRunning && !occupiesPublishSlot(status) && Boolean(entitlement) && !hasPublishSlotRemaining(entitlement);
  const isDatabase = isDatabaseReadyApp(instance.kind);
  const iconUrl = readyAppAssetUrl(instance.catalogKey, 'icon.svg');
  const cpuLimit = Number(stats?.cpuLimit || instance.cpuLimit || 1);
  const memoryLimit = Number(stats?.memoryLimitMb || instance.memoryMb || 1024);
  const cpuUsed = stats?.usageAvailable ? Number(stats.cpuPercent || 0) : null;
  const ramUsed = stats?.usageAvailable ? Number(stats.memoryUsedMb || 0) : null;

  const startRename = () => {
    setTitleDraft(title);
    setEditingTitle(true);
    setActionError('');
  };

  const saveTitle = async () => {
    const nextTitle = titleDraft.trim();
    if (!nextTitle || nextTitle === title) {
      setEditingTitle(false);
      return;
    }

    const saved = await runAction('rename', () => renameMarketplaceAppApi(instance.id, nextTitle));
    if (saved) {
      setEditingTitle(false);
    }
  };

  const saveDomain = async () => {
    const next = domainDraft.trim().toLowerCase();
    if (!next || next === instance.subdomain) {
      setEditingDomain(false);
      return;
    }

    if (!SUBDOMAIN_REGEX.test(next)) {
      setActionError(t('readyAppsSubdomainInvalid'));
      return;
    }

    const saved = await runAction('domain', () => changeMarketplaceAppDomainApi(instance.id, next));
    if (saved) {
      setEditingDomain(false);
    }
  };

  const saveResources = async () => {
    await runAction('resources', () => updateMarketplaceAppResourcesApi(instance.id, {
      cpuLimit: cpuDraft,
      memoryMb: memoryDraft
    }), { poll: true });
  };

  return (
    <DashboardLayout
      title={title}
      startActions={
        <Link
          to="/cloud"
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-950 dark:text-neutral-300 dark:hover:text-white"
        >
          <BackIcon size={16} />
          {t('readyAppsBack')}
        </Link>
      }
    >
      <div className="space-y-5">
        {actionError ? (
          <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
            {actionError}
          </p>
        ) : null}
        {startNeedsSlot ? (
          <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
            {t('readyAppsStartNeedsSlot')}
          </p>
        ) : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] dark:border-neutral-800 dark:bg-neutral-950 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-4">
                <img src={iconUrl} alt="" className="h-12 w-12 shrink-0 rounded-2xl" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {editingTitle ? (
                      <form
                        className="flex min-w-0 flex-1 items-center gap-2"
                        onSubmit={(event) => {
                          event.preventDefault();
                          saveTitle();
                        }}
                      >
                        <input
                          value={titleDraft}
                          onChange={(event) => setTitleDraft(event.target.value)}
                          maxLength={80}
                          autoFocus
                          disabled={busy === 'rename'}
                          className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-semibold text-neutral-950 outline-none focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
                        />
                        <Button type="submit" className="px-3 py-1.5" disabled={busy === 'rename' || !titleDraft.trim()}>
                          {busy === 'rename' ? <LoaderCircle size={16} className="animate-spin" /> : t('readyAppsRenameSave')}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="px-3 py-1.5"
                          disabled={busy === 'rename'}
                          onClick={() => setEditingTitle(false)}
                        >
                          {t('cancel')}
                        </Button>
                      </form>
                    ) : (
                      <>
                        <h1 className="text-lg font-semibold text-neutral-950 dark:text-white">{title}</h1>
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-900 dark:hover:text-white"
                          onClick={startRename}
                          aria-label={t('readyAppsRename')}
                          title={t('readyAppsRename')}
                        >
                          <Pencil size={15} />
                        </button>
                      </>
                    )}
                    <span className={classNames('rounded-full px-2.5 py-1 text-xs font-semibold', statusTone(instance.status))}>
                      {statusLabel(instance.status, t)}
                    </span>
                  </div>
                  {isPending ? (
                    <p className="mt-2 text-sm leading-6 text-neutral-500">{t('readyAppsStartingHint')}</p>
                  ) : null}
                  {instance.error ? <p className="mt-2 text-sm text-rose-600">{instance.error}</p> : null}
                </div>
              </div>

              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <dt className="text-xs font-medium text-neutral-500">{t('readyAppsAddressLabel')}</dt>
                  <dd className="mt-1.5">
                    {editingDomain ? (
                      <form
                        className="flex flex-wrap items-center gap-2"
                        onSubmit={(event) => {
                          event.preventDefault();
                          saveDomain();
                        }}
                      >
                        <input
                          value={domainDraft}
                          onChange={(event) => setDomainDraft(event.target.value)}
                          autoFocus
                          disabled={busy === 'domain'}
                          className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 font-mono text-sm outline-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
                        />
                        <Button type="submit" className="px-3 py-1.5" disabled={busy === 'domain' || !domainDraft.trim()}>
                          {busy === 'domain' ? <LoaderCircle size={16} className="animate-spin" /> : t('readyAppsDomainSave')}
                        </Button>
                        <Button type="button" variant="secondary" className="px-3 py-1.5" onClick={() => setEditingDomain(false)}>
                          {t('cancel')}
                        </Button>
                      </form>
                    ) : (
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span dir="ltr" className="truncate font-mono text-sm text-neutral-900 dark:text-neutral-100">
                          {instance.url || instance.fqdn || '—'}
                        </span>
                        {instance.url || instance.fqdn ? (
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-900 dark:hover:text-white"
                            onClick={() => copyValue('address', instance.url || instance.fqdn)}
                            aria-label={t('readyAppsCopyAddress')}
                            title={copied === 'address' ? t('readyAppsCopied') : t('readyAppsCopyAddress')}
                          >
                            {copied === 'address' ? <Check size={15} /> : <Copy size={15} />}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-900 dark:hover:text-white"
                          onClick={() => {
                            setDomainDraft(instance.subdomain);
                            setEditingDomain(true);
                            setActionError('');
                          }}
                          aria-label={t('readyAppsDomainEdit')}
                          title={t('readyAppsDomainEdit')}
                        >
                          <Pencil size={15} />
                        </button>
                      </div>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-neutral-500">{t('readyAppsCreatedLabel')}</dt>
                  <dd className="mt-1.5 text-sm text-neutral-900 dark:text-neutral-100">
                    {instance.createdAt
                      ? formatDate(instance.createdAt, { dateStyle: 'medium', timeStyle: 'short' })
                      : '—'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
              {instance.url ? (
                <a href={instance.url} target="_blank" rel="noreferrer">
                  <Button type="button" variant="secondary">
                    <ExternalLink size={16} />
                    {t('readyAppsOpenSite')}
                  </Button>
                </a>
              ) : null}
              {isRunning ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => runAction('stop', () => stopMarketplaceAppApi(instance.id), { poll: true })}
                >
                  {busy === 'stop' ? <LoaderCircle size={16} className="animate-spin" /> : <PowerOff size={16} />}
                  {t('readyAppsStop')}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={isBusy || startNeedsSlot}
                  title={startNeedsSlot ? t('readyAppsStartNeedsSlot') : undefined}
                  onClick={() => runAction('start', () => startMarketplaceAppApi(instance.id), { poll: true })}
                >
                  {busy === 'start' ? <LoaderCircle size={16} className="animate-spin" /> : <Power size={16} />}
                  {t('readyAppsStart')}
                </Button>
              )}
              <Button
                type="button"
                variant="danger"
                disabled={isBusy}
                onClick={async () => {
                  if (!window.confirm(t('readyAppsDeleteConfirm'))) {
                    return;
                  }
                  await runAction('delete', async () => {
                    await deleteMarketplaceAppApi(instance.id);
                    navigate('/cloud');
                    return null;
                  });
                }}
              >
                {busy === 'delete' ? <LoaderCircle size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {t('readyAppsDelete')}
              </Button>
            </div>
          </div>
        </section>

        {isDatabase && instance.connection ? (
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
            <h2 className="text-sm font-semibold">{t('readyAppsConnectionTitle')}</h2>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                ['host', t('readyAppsConnectionHost'), instance.connection.host],
                ['port', t('readyAppsConnectionPort'), instance.connection.port],
                ['user', t('readyAppsConnectionUser'), instance.connection.username],
                ['password', t('readyAppsConnectionPassword'), instance.connection.password],
                ['db', t('readyAppsConnectionDatabase'), instance.connection.databaseName]
              ].filter(([, , value]) => value).map(([key, label, value]) => (
                <div key={key} className="rounded-xl bg-neutral-50 px-3.5 py-3 dark:bg-neutral-900">
                  <dt className="text-xs text-neutral-500">{label}</dt>
                  <dd className="mt-1 flex items-center gap-1.5">
                    <span dir="ltr" className="truncate font-mono text-sm">{value}</span>
                    <button
                      type="button"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-800"
                      onClick={() => copyValue(key, value)}
                      aria-label={t('readyAppsCopySecret')}
                    >
                      {copied === key ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
          <h2 className="text-sm font-semibold">{t('readyAppsStatsTitle')}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs text-neutral-500">{t('readyAppsCpuLabel')}</p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                <div className="h-full bg-sky-500" style={{ width: `${cpuUsed == null ? 0 : Math.min(100, cpuUsed)}%` }} />
              </div>
              <p className="mt-2 text-sm" dir="ltr">
                {cpuUsed == null ? `0 / ${cpuLimit}` : `${cpuUsed}% / ${cpuLimit}`}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">{t('readyAppsRamLabel')}</p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                <div className="h-full bg-violet-500" style={{ width: `${usagePercent(ramUsed, memoryLimit)}%` }} />
              </div>
              <p className="mt-2 text-sm" dir="ltr">
                {ramUsed == null ? `0 / ${memoryLimit} MB` : `${Math.round(ramUsed)} / ${memoryLimit} MB`}
              </p>
            </div>
          </div>
          {!stats?.usageAvailable ? (
            <p className="mt-3 text-xs text-neutral-500">{t('readyAppsStatsUnavailable')}</p>
          ) : null}
        </section>

        <section className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
          <h2 className="text-sm font-semibold">{t('readyAppsResourcesTitle')}</h2>
          <div className="mt-4 space-y-4">
            <label className="block space-y-2">
              <span className="flex justify-between text-sm text-neutral-500">
                <span>{t('readyAppsCpuLabel')}</span>
                <span dir="ltr">{t('readyAppsCpuHint', { value: cpuDraft })}</span>
              </span>
              <input
                type="range"
                min="0.25"
                max="8"
                step="0.25"
                value={cpuDraft}
                disabled={isBusy}
                onChange={(event) => setCpuDraft(Number(event.target.value))}
                className="w-full"
              />
            </label>
            <label className="block space-y-2">
              <span className="flex justify-between text-sm text-neutral-500">
                <span>{t('readyAppsRamLabel')}</span>
                <span dir="ltr">{t('readyAppsRamHint', { value: memoryDraft })}</span>
              </span>
              <input
                type="range"
                min="256"
                max="8192"
                step="256"
                value={memoryDraft}
                disabled={isBusy}
                onChange={(event) => setMemoryDraft(Number(event.target.value))}
                className="w-full"
              />
            </label>
            <Button type="button" disabled={isBusy} onClick={saveResources}>
              {busy === 'resources' ? <LoaderCircle size={16} className="animate-spin" /> : null}
              {busy === 'resources' ? t('readyAppsResourcesSaving') : t('readyAppsResourcesSave')}
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950">
          <h2 className="text-sm font-semibold">{t('readyAppsVolumesTitle')}</h2>
          {instance.volumes?.length ? (
            <ul className="mt-3 space-y-2 text-sm text-neutral-600 dark:text-neutral-300">
              {instance.volumes.map((volume) => (
                <li key={volume.name}>
                  <span dir="ltr" className="font-mono text-neutral-900 dark:text-neutral-100">{volume.name}</span>
                  {' — '}
                  {language === 'fa' ? volume.purposeFa : volume.purposeEn}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-neutral-500">{t('readyAppsVolumesEmpty')}</p>
          )}
        </section>

        <AppInstanceLogs instanceId={instance.id} />
      </div>
    </DashboardLayout>
  );
};
